// © 2026 AttainIP Engineering

/**
 * Single-source-of-truth for session restore from the persisted refresh
 * token. Both ``RequireAuth`` (route guard) and the App-level mount
 * effect (``useAuthBootstrap``) call into this helper so we don't end
 * up with two divergent restore paths.
 *
 * The function is *idempotent*: a single in-flight promise is shared
 * across overlapping callers so we can't trigger two refresh requests
 * during a router race.
 */
import { useAuthStore, getStoredRefreshToken } from '../store/auth';
import { refreshToken } from '../api/identity';
import { ApiRequestError } from '../api/client';
import { adoptSession } from './adoptSession';

/**
 * Discriminated outcome for a restore attempt.
 *
 * Reactive callers (handleUnauthorized, authedFetch) redirect on any non-ok.
 * The proactive watcher (useSessionExpiryWatcher) distinguishes — `network`
 * gets one short retry before the user is signed out, so a Wi-Fi blip
 * partway through a session doesn't kick them to login.
 *
 * See RFC-0202 §"Session-Loss UX" §2b.
 */
export type RestoreResult =
  | { ok: true }
  | { ok: false; reason: 'no-stored-token' | 'rejected' | 'network' };

let inflight: Promise<RestoreResult> | null = null;

interface RestoreOptions {
  /** Force a fresh refresh even when the store already has a token.
   *  Used by callers that have observed an expired or rejected token
   *  and need to swap it for a new one before retrying. */
  force?: boolean;
}

/**
 * Try to populate the auth store from the persisted refresh token.
 *
 * Returns a discriminated ``RestoreResult`` — ``{ ok: true }`` on success,
 * or ``{ ok: false, reason }`` classifying the failure. Never throws.
 *
 * Pass ``{ force: true }`` to bypass the "already authenticated" early-
 * return — needed when the token in the store is past its ``expiresAt``
 * or the server has just rejected it.
 */
export async function restoreSessionFromRefreshToken(
  opts: RestoreOptions = {},
): Promise<RestoreResult> {
  const state = useAuthStore.getState();

  if (!opts.force && state.isAuthenticated && state.accessToken && !state.isTokenExpired()) {
    // Already authenticated with a still-valid token — nothing to do.
    return { ok: true };
  }

  if (inflight) return inflight;

  const storedToken = getStoredRefreshToken();
  if (!storedToken) return { ok: false, reason: 'no-stored-token' };

  inflight = (async (): Promise<RestoreResult> => {
    const { setLoading } = useAuthStore.getState();
    // Don't toggle isLoading for a background refresh of an already-authenticated
    // session — that would unmount the protected subtree (see SS-108).
    const alreadyAuthenticated = state.isAuthenticated && !!state.accessToken;
    if (!alreadyAuthenticated) setLoading(true);
    try {
      const tokenResponse = await refreshToken(storedToken);
      // Fetch the profile and write the session — the same step the login
      // callback and the organisation switch take (RFC-0510 W39), so the three
      // cannot disagree about which fields ride on a session.
      await adoptSession(tokenResponse);
      return { ok: true };
    } catch (err) {
      // ApiRequestError = server reached + said no (4xx/5xx) → session is dead.
      // Anything else (TypeError "Failed to fetch", AbortError, DNS) is transient.
      if (err instanceof ApiRequestError) {
        return { ok: false, reason: 'rejected' };
      }
      return { ok: false, reason: 'network' };
    } finally {
      if (!alreadyAuthenticated) setLoading(false);
      inflight = null;
    }
  })();

  return inflight;
}
